import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { DOCUMENT, DatePipe } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { Subject } from "rxjs";
import { filter, finalize, takeUntil } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { NewsService } from "../../../core";
import { ImageSEO } from "../../../core/models/image-seo.model";
import { ProductService } from "../../../core/services/products.service";
import { SiilaTranslationService } from "../../../core/services/siila-translation.service";
import { StaticImagesSEOService } from "../../../core/services/static-images.service";
import { MoreNews } from "../../models/more-news.model";
import { NewsSearch } from "../../models/news-search.model";
import { NewsTopics } from "../../models/news-topics.model";
import { ContentTags, StateTag } from "../../models/news.model";
import { I18nService } from "../../services";
import { AnalyticsService } from "../../services/analytics.service";
import { MetaTagService } from "../../services/meta-tag.service";
import { PrerenderReadyService } from "../../services/prerender-ready.service";
import { UrlCanonicalService } from "../../services/url-canonical.service";
import {
  NEWS_LAST_CLICKED_KEY,
  NEWS_LOADED_COUNT_KEY,
  NewsNavigateService
} from "../../services/news.navigate.service";
import { PWAService } from "../../services/pwa-service";
import { InstallPromptComponent } from "../dialogs";
import { ExclusiveContentModalComponent } from "../exclusive-access/exclusive-content-modal/exclusive-content-modal.component";

/**
 * The fields every tag collection on an article shares (`productTags`,
 * `stateTags`, `propertyTypeTags`), which is all the tag-name lookup needs.
 */
interface NamedTag {
  id: number;
  name: string;
}

/** GA4 `page_context` for the REsource banners on this page. */
const RESOURCE_HOME_PAGE_CONTEXT = "resource_home";

@Component({
  selector: "app-home-news",
  templateUrl: "./home-news.component.html",
  styleUrls: ["./home-news.component.scss"]
})
export class HomeNewsComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  currentCode: string;
  isExpanded: boolean = false;
  isMobile = false;
  news: NewsSearch;
  mostRead: MoreNews[] = [];
  topics: NewsTopics[] = [];
  /** First block of the list; a multiple of PAGE_SIZE so later pages line up with it. */
  private readonly INITIAL_SIZE = 25;
  /** News appended per "load more" click. */
  private readonly PAGE_SIZE = 5;
  /** Most news restored at once when coming back from an article (backend caps per_page at 50). */
  private readonly MAX_RESTORE_SIZE = 50;
  /** Next page to request with PAGE_SIZE; nextPage × PAGE_SIZE is the offset of the next news. */
  private nextPage = 0;
  total = 0;
  /** The tag id from the URL, always the raw string segment. */
  tagIdFilter: string = null;
  email: string = "";
  keyword = "";
  tagName: string = null;
  color: string;
  /**
   * The literal segment this listing is addressed by, after normalization —
   * `tags` or `topic`. Only `topic` is FUNCTIONAL (it switches the search to
   * topic mode), so canonicalization preserves it; the legacy singular `tag` is
   * folded into `tags` by `normalizeTagSegment`.
   */
  tagsSegment: string = null;
  innerWidth: number;
  browserLang: string;
  marketAnalyticsSEO: ImageSEO;
  spotTransformingSEO: ImageSEO;
  siilaAcademyBannerSEO: ImageSEO;
  loadingNews: boolean = true;
  /** True while a "load more" request is in flight; disables the button. */
  loadingMore: boolean = false;
  alreadyLoaded: boolean = false;
  private prerenderMarked = false;
  /**
   * Set while this component is sending the visitor to `/404`, so the
   * reload-on-NavigationEnd below leaves that navigation alone.
   */
  private navigatingToNotFound: boolean = false;
  private readonly prerenderStart = performance.now(); // component construction time
  show = true;
  showInstall = true;
  isTopic = false;
  bannerOod: ImageSEO;
  bannerPairs: ImageSEO;
  topBannerSEO: ImageSEO;
  latamCountry = "latamBr";
  private readonly destroy$ = new Subject<void>();

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.innerWidth = window.innerWidth;
    if (innerWidth > 900) {
      this.isMobile = false;
    } else {
      this.isMobile = true;
    }
  }

  constructor(
    private newsService: NewsService,
    private i18: I18nService,
    public breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private productService: ProductService,
    private snackBar: MatSnackBar,
    private siilaTranslationService: SiilaTranslationService,
    private datePipe: DatePipe,
    private metaSrv: MetaTagService,
    private staticImgSrv: StaticImagesSEOService,
    private analyticsSrv: AnalyticsService,
    private router: Router,
    private newsNavigate: NewsNavigateService,
    private matDialog: MatDialog,
    private pwaService: PWAService,
    private ngxLogger: NGXLogger,
    @Inject(DOCUMENT) private document: Document,
    private prerenderReadyService: PrerenderReadyService,
    private urlCanonical: UrlCanonicalService
  ) {
    this.currentCode = environment.platformId;
    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
    if (this.currentCode == "latam") {
      this.browserLang = "latam";
    } else {
      this.browserLang = this.siilaTranslationService.getCurrentLanguage();
    }
  }

  ngAfterViewInit(): void {
    // Keep the list covered while a scroll restore is pending; restoreListScroll
    // uncovers it once the news are rendered.
    this.show = !localStorage.getItem("scrollPosition");
  }

  ngAfterViewChecked(): void {
    // Signal Prerender.io once the news list has actually loaded. Runs every
    // change-detection cycle, so gate on the data flag and a one-shot boolean.
    if (this.alreadyLoaded && !this.prerenderMarked) {
      this.prerenderMarked = true;
      const elapsedMs = Math.round(performance.now() - this.prerenderStart);
      this.ngxLogger.debug(
        `Prerender: ready after ${elapsedMs}ms for ${this.router.url}`
      );
      this.prerenderReadyService.signalReady();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.staticImgSrv.advanceBannerRotation();
    this.bannerOod = this.staticImgSrv.getBannerOdd();
    this.bannerPairs = this.staticImgSrv.getBannerPairs();
    if (this.currentCode !== "co") {
      this.analyticsSrv.trackBannerImpression(
        this.bannerOod,
        1,
        this.currentCode,
        "resource_banner",
        RESOURCE_HOME_PAGE_CONTEXT
      );
      this.analyticsSrv.trackBannerImpression(
        this.bannerPairs,
        2,
        this.currentCode,
        "resource_banner",
        RESOURCE_HOME_PAGE_CONTEXT
      );
    }
    if (this.currentCode === "br") {
      this.topBannerSEO = this.staticImgSrv.getTopLeaderboardBanner();
      this.analyticsSrv.trackBannerImpression(
        this.topBannerSEO,
        1,
        this.currentCode,
        "resource_top_banner",
        RESOURCE_HOME_PAGE_CONTEXT
      );
    }
    this.setIcons();
    setTimeout(() => {
      this.showInstall = false;
    }, 15000);

    this.setLanguageLatam();
    // Reload only while this component is still the one on screen (route reused
    // with new params). `takeUntil` drops the listener on destroy, so navigating
    // AWAY (e.g. an article `routerLink`) stays a client-side navigation instead
    // of reloading the app — and no stale listener reloads later navigations.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // `/404` is rendered by PageNotFoundComponent, which is what emits the
        // `prerender-status-code` meta. Reloading on top of it would throw that
        // away and re-request the bad URL.
        if (this.navigatingToNotFound) {
          return;
        }
        window.location.reload();
      });
    this.marketAnalyticsSEO = this.staticImgSrv.getMultifamilySEO();
    this.spotTransformingSEO = this.staticImgSrv.getSpotTransformingExperienceSEO();
    this.siilaAcademyBannerSEO = this.staticImgSrv.getAcademyImgSEO();
    this.route.params.subscribe(param => {
      if (param.id) {
        // `tags`/`tag`/`topic` are literal route segments, so the segment the
        // visitor arrived on comes from the route's `data`, not from a param.
        // It is absent on the legacy `insights/tags/:id/:type/:color` shape.
        const tagsSegment: string = this.route.snapshot.data?.tagsSegment;

        // A tag URL only means something when the id and colour segments are
        // numbers — a tag id and a palette index. Anything else is not a page:
        // it used to render this shell with a 200 and print the junk segment as
        // the on-screen label.
        if (tagsSegment && !this.hasValidTagParams(param)) {
          this.navigateToNotFoundPage();
          return;
        }

        this.isTopic = tagsSegment === "topic";
        this.tagIdFilter = param.id;
        this.tagName = param.type;
        this.color = param.color;
        this.tagsSegment = tagsSegment;
        this.normalizeTagSegment();
      }
      this.metaSrv.setInsightsHomeTags();
      this.getNews();
      this.getMostNews();
      this.getTopics();
    });

    setTimeout(() => {
      this.configureWistiaAutoplay();
    }, 2000);
  }

  configureWistiaAutoplay(): void {
    if (typeof (window as any).Wistia !== "undefined") {
      if (this.bannerOod?.type === "wistia" && this.bannerOod.wistiaId) {
        this.tryPlayWistiaVideo(this.bannerOod.wistiaId, 0);
      }

      if (this.bannerPairs?.type === "wistia" && this.bannerPairs.wistiaId) {
        this.tryPlayWistiaVideo(this.bannerPairs.wistiaId, 0);
      }
    } else {
      setTimeout(() => {
        this.configureWistiaAutoplay();
      }, 1000);
    }
  }

  tryPlayWistiaVideo(videoId: string, attempt: number): void {
    if (attempt >= 10) {
      return;
    }

    const wistia = (window as any).Wistia;
    const video = wistia.api(videoId);

    if (video) {
      video.mute();
      video.play();
      video.bind("end", () => {
        video.time(0);
        video.play();
      });
    } else {
      setTimeout(() => {
        this.tryPlayWistiaVideo(videoId, attempt + 1);
      }, 500);
    }
  }

  setLanguageLatam() {
    const url = this.router.url;
    if (this.currentCode == "latam" && url.includes("/es")) {
      const newUrl = url.replace("/es", "/en");
      window.location.href = newUrl;
    }
  }

  getAltTxt(text: string) {
    return this.siilaTranslationService.getTranslation(text);
  }

  /**
   * Loads the first block of the list (INITIAL_SIZE, or the size restored on
   * mobile when coming back from an article) and replaces whatever was shown.
   * Fixed sizes keep the request identical across visitors, so the backend
   * search cache serves it.
   */
  getNews() {
    this.loadingNews = true;
    const initialSize = this.getInitialSize();

    this.fetchNewsPage(0, initialSize).subscribe(
      (resp: NewsSearch) => {
        this.news = resp;
        this.nextPage = initialSize / this.PAGE_SIZE;
        this.loadingNews = false;
        this.alreadyLoaded = true;
        this.total = resp.totalElements;
        this.restoreListScroll();

        // A tag listing with nothing behind it is not a page: the id in the URL
        // matches no tag, or no published article carries it, and the shell
        // would render empty under a 200. Judged only for tag/topic URLs — the
        // `/resource` hub legitimately renders whatever it has, and `seeMore`
        // only ever grows a listing that already had results.
        if (this.tagsSegment && this.total === 0) {
          this.navigateToNotFoundPage();
          return;
        }

        this.canonicalizeTagUrl();
      },
      error => {
        this.loadingNews = false;
        this.restoreListScroll();
        this.ngxLogger.error("Error loading news list", error);
      }
    );
  }

  /**
   * "Load more": fetches only the next PAGE_SIZE news and appends them. Ignored
   * while a request is in flight, so repeated clicks send a single request.
   */
  seeMore() {
    if (this.loadingMore || !this.canLoadMore) {
      return;
    }
    this.loadingMore = true;

    this.fetchNewsPage(this.nextPage, this.PAGE_SIZE)
      .pipe(finalize(() => (this.loadingMore = false)))
      .subscribe(
        (resp: NewsSearch) => {
          // The first page lifts the featured news to the top; it can show up
          // again further down, so skip ids already on screen.
          const shownIds = new Set(this.news.content.map(item => item.id));
          this.news.content = this.news.content.concat(
            resp.content.filter(item => !shownIds.has(item.id))
          );
          this.total = resp.totalElements;
          this.nextPage++;
        },
        error => this.ngxLogger.error("Error loading more news", error)
      );
  }

  /** True while the backend still has news past the ones requested so far. */
  get canLoadMore(): boolean {
    return this.nextPage * this.PAGE_SIZE < this.total;
  }

  /**
   * Size of the first request: INITIAL_SIZE, or on mobile the amount the
   * reader had loaded before opening an article, rounded up to a whole number
   * of PAGE_SIZE blocks so "load more" keeps lining up with it.
   */
  private getInitialSize(): number {
    const restored = Number(localStorage.getItem(NEWS_LOADED_COUNT_KEY));
    if (!restored || restored <= this.INITIAL_SIZE) {
      return this.INITIAL_SIZE;
    }
    const blocks = Math.ceil(
      Math.min(restored, this.MAX_RESTORE_SIZE) / this.PAGE_SIZE
    );
    return blocks * this.PAGE_SIZE;
  }

  /**
   * On mobile, coming back from an article: scrolls to the news the reader
   * opened (or to the saved offset when it is not in the list), once the list
   * is rendered and again after images above it have shifted the layout.
   */
  private restoreListScroll(): void {
    const scroll = localStorage.getItem("scrollPosition");
    if (scroll === null) {
      return;
    }
    const newsId = localStorage.getItem(NEWS_LAST_CLICKED_KEY);
    localStorage.removeItem("scrollPosition");
    localStorage.removeItem(NEWS_LOADED_COUNT_KEY);
    localStorage.removeItem(NEWS_LAST_CLICKED_KEY);

    const scrollBack = () => {
      const card = newsId
        ? document.querySelector(`[data-news-id="${newsId}"]`)
        : null;
      if (card) {
        card.scrollIntoView({ block: "center" });
      } else {
        window.scrollTo(0, Number(scroll));
      }
      this.show = true;
    };
    setTimeout(scrollBack);
    setTimeout(scrollBack, 500);
  }

  private fetchNewsPage(page: number, perPage: number) {
    return this.newsService.getNewsSearch(
      this.browserLang,
      page,
      perPage,
      null,
      this.tagIdFilter,
      this.getRegionOrigin(),
      null,
      this.isTopic
    );
  }

  private getRegionOrigin(): number {
    let regionOrigin = null;
    if (Number(this.tagIdFilter) === 20 && this.currentCode == "latam") {
      const countryBR = this.siilaTranslationService.getTranslationFromJSONFiles(
        "news.brasil"
      );
      const countryMX = this.siilaTranslationService.getTranslationFromJSONFiles(
        "news.mexico"
      );
      regionOrigin =
        this.tagName.toLowerCase() == countryBR.toLowerCase()
          ? 101
          : this.tagName.toLowerCase() == countryMX.toLowerCase()
          ? 102
          : null;
    }
    return regionOrigin;
  }

  getMostNews() {
    this.newsService.getMostNews().subscribe(resp => {
      this.mostRead = resp as MoreNews[];
    });
  }

  getTopics() {
    this.newsService.getNewsTopics().subscribe(resp => {
      this.topics = resp as NewsTopics[];
      this.canonicalizeTagUrl();
    });
  }

  /**
   * A tag URL is a real page only when its id and colour segments are numeric —
   * they are a tag id and a palette index (`/resource/tags/Nacional/5/2`).
   * Anything else has nothing behind it.
   */
  private hasValidTagParams(param: Params): boolean {
    return /^\d+$/.test(param.id) && /^\d+$/.test(param.color);
  }

  /**
   * Hands the visitor to `PageNotFoundComponent`, which is what emits
   * `prerender-status-code: 404` and signals prerender-ready. `badURL` carries
   * the address that failed, for the message that page prints.
   */
  private navigateToNotFoundPage(): void {
    this.navigatingToNotFound = true;
    this.loadingNews = false;
    this.router.navigateByUrl("/404", {
      state: { badURL: window.location.href }
    });
  }

  /**
   * Fold the legacy singular `resource/tag/...` onto `resource/tags/...`.
   *
   * Both segments reach this component and behave identically — only `topic`
   * changes what is searched — so the singular is just a second address for one
   * page, and duplicate content to a crawler. It exists because
   * `DetailNewsComponent.goNewsTags` minted it while `NewsNavigateService`
   * minted the plural; the app now emits only `tags`, and this redirects the
   * URLs already in the wild.
   *
   * Separate from `canonicalizeTagUrl` because it needs no loaded data: the
   * segment is wrong on sight, and only the segment changes. The name is passed
   * through untouched, so this is safe even for tag 20, whose name still selects
   * the region in `getNews`.
   */
  private normalizeTagSegment(): void {
    if (this.tagsSegment !== "tag") {
      return;
    }
    this.tagsSegment = "tags";
    this.urlCanonical.apply(
      this.siilaTranslationService.buildLocalizedPath(
        `/resource/tags/${this.tagName}/${this.tagIdFilter}/${this.color}`
      )
    );
  }

  /**
   * Tag pages are `resource/<tags|topic>/<tag name>/<id>/<color>`, where the name
   * is written in the reader's language (`/resource/tags/Nacional/19/2` vs
   * `/resource/tags/National/19/2/lang/en`). Only the id filters the search, so a
   * name in the wrong language — or any name at all — still renders the page, and
   * the template prints that segment as the on-screen label. This rebuilds the
   * name from the id, so the URL, the label and the content agree, and crawlers
   * get a 301 onto the one form instead of a 200 per spelling.
   *
   * Deliberately does nothing when the name cannot be resolved from data already
   * on the page: guessing a name would be worse than leaving the URL alone.
   */
  private canonicalizeTagUrl(): void {
    // `tagsSegment` is absent on the legacy `insights/tags/:id/:type/:color`
    // shape, whose segments are ordered differently; leave those untouched.
    if (!this.tagIdFilter || !this.tagsSegment || !this.color) {
      return;
    }
    // Tag 20 is the "national" tag: `getNews` derives regionOrigin by comparing
    // the URL's name against the translated country names, so rewriting it would
    // change WHICH articles the page shows. Off limits.
    if (Number(this.tagIdFilter) === 20) {
      return;
    }

    const resolvedName = this.resolveTagName();
    if (!resolvedName) {
      return;
    }

    const canonicalPath = this.siilaTranslationService.buildLocalizedPath(
      `/resource/${this.tagsSegment}/${resolvedName}/${this.tagIdFilter}/${this.color}`
    );
    const canonicalUrl = this.urlCanonical.apply(canonicalPath);
    this.metaSrv.addCanonicalTag(canonicalUrl);
    // Keep the visible label in step with the corrected URL.
    this.tagName = resolvedName;
  }

  /**
   * The tag's name in the active language, resolved from the id against data the
   * page has already loaded — the topic list in topic mode, otherwise the tags
   * carried by the filtered articles themselves. Returns null when the id is not
   * found (e.g. a tag type this page never lists), which suppresses rewriting.
   */
  private resolveTagName(): string {
    const id = Number(this.tagIdFilter);

    if (this.isTopic) {
      const topic = (this.topics || []).find(t => Number(t.id) === id);
      return topic
        ? this.siilaTranslationService.getTranslation(topic.name)
        : null;
    }

    const articles = (this.news && this.news.content) || [];
    for (const article of articles) {
      // Structural typing on purpose: `contentTags` has two competing model
      // definitions in this codebase (the search model omits `productTags`), and
      // the lookup only ever needs id + name.
      const tags = article.contentTags as {
        productTags?: NamedTag[];
        propertyTypeTags?: NamedTag[];
        stateTags?: NamedTag[];
      };
      if (!tags) {
        continue;
      }
      const match: NamedTag = [
        ...(tags.propertyTypeTags || []),
        ...(tags.stateTags || []),
        ...(tags.productTags || [])
      ].find(tag => tag && Number(tag.id) === id);
      if (match) {
        return this.siilaTranslationService.getTranslation(match.name);
      }
    }
    return null;
  }

  headerImageNews(item: string, size = false): string {
    const url = item.replace("***", size ? "1280w" : "640w");
    return `${environment.cloudfrontURL}${url}`;
  }

  openAcademy() {
    window.open(
      "https://mailchi.mp/siila/academy-advanced",
      this.isMobile ? "_self" : "_blank"
    );
  }

  /** Card links navigate via `routerLink`; this only keeps the mobile scroll restore. */
  rememberListScroll(newsId?: number): void {
    this.newsNavigate.rememberListScroll(
      this.isMobile,
      this.nextPage * this.PAGE_SIZE,
      newsId
    );
  }

  openNewsSearch() {
    this.newsNavigate.openNewsSearch(this.isMobile, this.keyword);
  }

  validEmail() {
    var EMAIL_REGEX = /\S+@\S+\.\S+/;

    if (this.email.match(EMAIL_REGEX)) {
      return false;
    } else {
      return true;
    }
  }

  public sendEmail() {
    let message = this.i18.get("global.newsForm.messageEmail");
    let action = this.i18.get("global.newsForm.action");
    let errorMsj = this.i18.get("global.newsForm.errorMsj");
    this.productService.sendEmail({ email: this.email }).subscribe(
      () => {
        this.snackBar.open(message, action);
        this.email = "";
      },
      () => {
        this.snackBar.open(errorMsj, action);
      }
    );
  }

  getLanguage() {
    return this.siilaTranslationService.getCurrentLanguage();
  }

  goNewsTags(tag, color, regionOrigin?: number) {
    this.newsNavigate.goNewsTags(
      tag,
      color,
      this.getNationalStateName(tag, regionOrigin),
      this.isMobile
    );
  }

  formatDate(date, format) {
    return this.datePipe.transform(date, format);
  }

  isEnglishLang() {
    return this.i18.getCurrentLanguage() === "en";
  }

  openSpot() {
    if (this.isBR()) {
      var url = this.isEnglishLang()
        ? "https://spot.siila.com.br/company/zolver/34161272/en"
        : "https://spot.siila.com.br/empresa/zolver/34161272/pt-br";
      window.open(url, this.isMobile ? "_self" : "_blank");
    } else {
      window.open(
        this.i18.buildLocalizedPath("/SPOT"),
        this.isMobile ? "_self" : "_blank"
      );
    }
  }

  openProduct() {
    window.open(
      this.i18.buildLocalizedPath("/marketanalytics"),
      this.isMobile ? "_self" : "_blank"
    );
  }

  openMultifamilyBanner() {
    let url = this.isEnglishLang()
      ? "https://mailchi.mp/siila.com.br/7gms21uue1"
      : "https://mailchi.mp/siila.com.br/multifamily";
    window.open(url, this.isMobile ? "_self" : "_blank");
  }

  goHome(latam = false) {
    this.newsNavigate.goHome(latam);
  }

  getStateTagName(contentTags: ContentTags) {
    const stateTag = contentTags.stateTags[0];
    return this.getNationalStateName(stateTag, contentTags.regionOrigin);
  }

  private getNationalStateName(stateTag: StateTag, regionOrigin: number) {
    let stateName = null;
    if (stateTag.id == 20 && regionOrigin) {
      const country = regionOrigin == 102 ? "mexico" : "brasil";
      stateName = this.siilaTranslationService
        .getTranslationFromJSONFiles("news." + country)
        .toLowerCase();
    } else {
      stateName = this.siilaTranslationService.getTranslation(stateTag.name);
    }

    return stateName;
  }

  isIOS(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  validateTitle(title) {
    if (title.match(/´/)) {
      return title.replace(/´/g, "'");
    } else {
      return title;
    }
  }

  installApp() {
    if (this.isIOS()) {
      this.matDialog.open(InstallPromptComponent);
    } else {
      this.pwaService.promptPWAInstall();
    }
  }

  isStandAlone() {
    return window.matchMedia("(display-mode: standalone)").matches;
  }

  isBR() {
    return this.currentCode && this.currentCode.toLowerCase() === "br";
  }

  closeInstall() {
    this.showInstall = false;
  }

  setIcons() {
    if (this.isIOS()) {
      const linkIcon = this.document.getElementById(
        "dynamic-192"
      ) as HTMLLinkElement | null;
      linkIcon.href = "/assets/icons/android-chrome-192x192-i2.png";

      const linkIcon2 = this.document.getElementById(
        "dynamic-144"
      ) as HTMLLinkElement | null;
      linkIcon2.href = "/assets/icons/android-chrome-144x144-i2.png";

      const linkIcon3 = this.document.getElementById(
        "dynamic-96"
      ) as HTMLLinkElement | null;
      linkIcon3.href = "/assets/icons/android-chrome-96x96-i2.png";

      const linkIcon4 = this.document.getElementById(
        "dynamic-72"
      ) as HTMLLinkElement | null;
      linkIcon4.href = "/assets/icons/android-chrome-72x72-i2.png";

      const linkIcon5 = this.document.getElementById(
        "dynamic-48"
      ) as HTMLLinkElement | null;
      linkIcon5.href = "/assets/icons/favicon-48x48-i2.png";
    } else {
      const linkIcon = this.document.getElementById(
        "dynamic-192-a"
      ) as HTMLLinkElement | null;
      linkIcon.href = "/assets/icons/android-chrome-192x192-i2.png";

      const linkIcon2 = this.document.getElementById(
        "dynamic-144-a"
      ) as HTMLLinkElement | null;
      linkIcon2.href = "/assets/icons/android-chrome-144x144-i2.png";

      const linkIcon3 = this.document.getElementById(
        "dynamic-96-a"
      ) as HTMLLinkElement | null;
      linkIcon3.href = "/assets/icons/android-chrome-96x96-i2.png";

      const linkIcon4 = this.document.getElementById(
        "dynamic-72-a"
      ) as HTMLLinkElement | null;
      linkIcon4.href = "/assets/icons/android-chrome-72x72-i2.png";

      const linkIcon5 = this.document.getElementById(
        "dynamic-48-a"
      ) as HTMLLinkElement | null;
      linkIcon5.href = "/assets/icons/favicon-48x48-i2.png";
    }
  }

  openBanner(
    banner: ImageSEO,
    position: number,
    placement: string = "resource_banner"
  ): void {
    if (this.currentCode !== "co") {
      this.analyticsSrv.trackBannerClick(
        banner,
        position,
        this.currentCode,
        placement,
        RESOURCE_HOME_PAGE_CONTEXT
      );
    }
    let resolvedLink = banner.link;
    if (
      resolvedLink &&
      !resolvedLink.startsWith("http") &&
      !resolvedLink.startsWith("/")
    ) {
      resolvedLink = "https://" + resolvedLink;
    }
    window.open(resolvedLink, this.isMobile ? "_self" : "_blank");
  }

  onVideoLoaded(video: HTMLVideoElement): void {
    video.muted = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(err => {
        console.warn("Autoplay blocked by the browser:", err);
      });
    }
  }

  openModal(): void {
    this.matDialog.open(ExclusiveContentModalComponent, {
      width: "800px",
      disableClose: false
    });
  }
}
